import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';

import { setAuthTokenProvider } from './api-auth';

export type AuthMode = 'supabase' | 'basic';
export type DeploymentMode = 'saas' | 'self-hosted';

function createBrowserClient(): SupabaseClient | null {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!url || !key) return null;
  return createClient(url, key);
}

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isConfigured: boolean;
  mode: AuthMode;
  /** Overall deployment flavor — gates marketing pages + SaaS-only UI. */
  deploymentMode: DeploymentMode;
  /** Public-facing base URL — use in docs/snippets/curl examples. */
  baseUrl: string;
  /** True when the admin password is the default 'admin' (basic mode only). */
  defaultPasswordWarning: boolean;
  signInWithOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  /** Basic-auth username/password sign-in. Throws on bad creds. */
  signInWithPassword: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

// ── Dispatcher ───────────────────────────────────────────────────────────────
// Fetches /api/auth/mode on mount to decide which provider to mount.
// Renders a minimal loading state so children never see a half-initialized
// auth context.

interface AuthModeResponse {
  mode: AuthMode;
  deployment_mode?: DeploymentMode;
  base_url?: string;
  default_password_warning?: boolean;
}

async function fetchAuthMode(): Promise<AuthModeResponse> {
  try {
    const res = await fetch('/api/auth/mode');
    if (!res.ok) throw new Error(`mode endpoint ${res.status}`);
    return (await res.json()) as AuthModeResponse;
  } catch {
    return { mode: 'supabase', deployment_mode: 'saas' };
  }
}

function defaultBaseUrl(deploymentMode: DeploymentMode): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return deploymentMode === 'saas' ? 'https://llmcontext.dev' : 'http://localhost:3000';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [modeInfo, setModeInfo] = useState<AuthModeResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchAuthMode().then((info) => {
      if (!cancelled) setModeInfo(info);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!modeInfo) {
    // Minimal shell — don't render children until we know the mode, so
    // useAuth() never returns before the right provider is mounted.
    return null;
  }

  const deploymentMode: DeploymentMode =
    modeInfo.deployment_mode ?? (modeInfo.mode === 'basic' ? 'self-hosted' : 'saas');
  const baseUrl = modeInfo.base_url?.replace(/\/$/, '') || defaultBaseUrl(deploymentMode);

  if (modeInfo.mode === 'basic') {
    return (
      <BasicAuthProvider
        deploymentMode={deploymentMode}
        baseUrl={baseUrl}
        defaultPasswordWarning={modeInfo.default_password_warning ?? false}
      >
        {children}
      </BasicAuthProvider>
    );
  }

  return (
    <SupabaseAuthProvider deploymentMode={deploymentMode} baseUrl={baseUrl}>
      {children}
    </SupabaseAuthProvider>
  );
}

// ── Supabase provider (SaaS mode) ───────────────────────────────────────────

function SupabaseAuthProvider({
  children,
  deploymentMode,
  baseUrl,
}: {
  children: ReactNode;
  deploymentMode: DeploymentMode;
  baseUrl: string;
}) {
  const [client] = useState<SupabaseClient | null>(() => createBrowserClient());
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!client) {
      setIsLoading(false);
      return;
    }

    void client.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [client]);

  const signInWithOtp = async (email: string): Promise<void> => {
    if (!client)
      throw new Error(
        'Supabase is not configured (missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)'
      );
    const { error } = await client.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (error) throw new Error(error.message);
  };

  const verifyOtp = async (email: string, token: string): Promise<void> => {
    if (!client) throw new Error('Supabase is not configured');
    const { error } = await client.auth.verifyOtp({ email, token, type: 'email' });
    if (error) throw new Error(error.message);
  };

  const signInWithPassword = async (): Promise<void> => {
    throw new Error('Password sign-in is only available when AUTH_MODE=basic');
  };

  const signOut = async (): Promise<void> => {
    await client?.auth.signOut();
  };

  const getToken = async (): Promise<string | null> => {
    if (!client) return null;
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  };

  useEffect(() => {
    setAuthTokenProvider(getToken);
    return () => setAuthTokenProvider(null);
    // getToken is stable across renders (closes over `client` set once).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider
      value={{
        user,
        isLoading,
        isConfigured: client !== null,
        mode: 'supabase',
        deploymentMode,
        baseUrl,
        defaultPasswordWarning: false,
        signInWithOtp,
        verifyOtp,
        signInWithPassword,
        signOut,
        getToken,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

// ── Basic-auth provider (self-host mode) ────────────────────────────────────

interface BasicAuthUserResponse {
  id: string;
  email: string;
  username: string;
}

function basicAuthUserToSupabaseShape(raw: BasicAuthUserResponse): User {
  // Construct a minimum User-shaped object so consumers that typecheck
  // against Supabase's `User` keep working without branching.
  return {
    id: raw.id,
    app_metadata: {},
    user_metadata: { username: raw.username },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
    email: raw.email,
  } as unknown as User;
}

function BasicAuthProvider({
  children,
  deploymentMode,
  baseUrl,
  defaultPasswordWarning,
}: {
  children: ReactNode;
  deploymentMode: DeploymentMode;
  baseUrl: string;
  defaultPasswordWarning: boolean;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Probe /api/me to see if we already have a valid session cookie.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/me');
        if (!res.ok) {
          if (!cancelled) setUser(null);
          return;
        }
        const data = (await res.json()) as { id?: string; email: string };
        if (!cancelled) {
          setUser(
            basicAuthUserToSupabaseShape({
              id: data.id ?? data.email,
              email: data.email,
              username: data.email,
            })
          );
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithPassword = async (username: string, password: string): Promise<void> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Login failed (${res.status})`);
    }
    const data = (await res.json()) as { user: BasicAuthUserResponse };
    setUser(basicAuthUserToSupabaseShape(data.user));
  };

  const signOut = async (): Promise<void> => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
    }
  };

  // Basic mode doesn't use Bearer tokens on the wire — the server reads
  // the session cookie. But existing API clients call getToken() and
  // attach the result as a Bearer header; the server ignores that header
  // in basic mode, so any non-null sentinel keeps the call path working.
  const getToken = async (): Promise<string | null> => (user ? 'basic' : null);

  useEffect(() => {
    setAuthTokenProvider(getToken);
    return () => setAuthTokenProvider(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const rejectSupabase = async (): Promise<void> => {
    throw new Error('Email OTP sign-in is only available when AUTH_MODE=supabase');
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        isLoading,
        isConfigured: true,
        mode: 'basic',
        deploymentMode,
        baseUrl,
        defaultPasswordWarning,
        signInWithOtp: rejectSupabase,
        verifyOtp: rejectSupabase,
        signInWithPassword,
        signOut,
        getToken,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
